import { forwardRef, useId, useImperativeHandle, useRef, useState } from 'react'

import MouseOver from '~/components/MouseOver/MouseOver'

import Spinner from '../../Spinner'

import PasswordVisibilityIcon from './PasswordVisibilityIcon'

import {
  StyledInput,
  InputStructure,
  LabelContainer,
  LabelLeftArea,
  Label,
  ComponentStructure,
  ActionContainer,
  RightAreaElementContainer
} from './styles'

import { InputProps } from './type'

const VariantMap = {
  input: 'input',
  textarea: 'textarea'
} as const

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      component = 'input',
      value,
      onChange,
      tabIndex,
      required,
      isLoading,
      isLoadingBlueprints,
      heightClass,
      statusClass,
      disabled = false,
      nonFocusable = false,
      informational = false,
      rightAreaElement,
      showValidationProcessingTime = false,
      id,
      type: inputType,
      actionButton,
      pulseAnimate,
      mouseOver,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false)

    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)

    const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(target.value)
    }

    const toggleShowPassword = () => {
      setShowPassword(!showPassword)
    }

    const autoId = useId()

    const inputId = id || autoId

    const renderPasswordVisibilityIcon = inputType === 'password'

    const type = inputType === 'password' && showPassword ? 'text' : inputType

    const showSpinnerInput = isLoading || isLoadingBlueprints

    return (
      <ComponentStructure>
        {label && (
          <LabelContainer heightClass={heightClass}>
            <LabelLeftArea heightClass={heightClass}>
              <Label
                heightClass={heightClass}
                required={required}
                htmlFor={inputId}
              >
                {label}
              </Label>

              {showValidationProcessingTime && (
                <Spinner
                  heightClass={heightClass}
                  data-testid='validation-processing-time'
                />
              )}
            </LabelLeftArea>

            {actionButton && <ActionContainer>{actionButton}</ActionContainer>}
          </LabelContainer>
        )}

        <InputStructure
          heightClass={heightClass}
          statusClass={statusClass}
          disabled={disabled}
          nonFocusable={nonFocusable}
          informational={informational}
          component={component}
          pulseAnimate={pulseAnimate}
        >
          <StyledInput
            id={inputId}
            ref={inputRef}
            value={value}
            onChange={handleChange}
            as={VariantMap[component]}
            heightClass={heightClass}
            disabled={disabled}
            readOnly={informational}
            required={required}
            tabIndex={nonFocusable ? -1 : tabIndex}
            informational={informational}
            type={type}
            {...props}
          />

          {component !== 'textarea' && (
            <RightAreaElementContainer heightClass={heightClass}>
              {showSpinnerInput && (
                <Spinner
                  heightClass={heightClass}
                  color={isLoadingBlueprints ? '#006BFF' : undefined}
                />
              )}

              {renderPasswordVisibilityIcon && (
                <PasswordVisibilityIcon
                  showingPassword={showPassword}
                  onTrigger={toggleShowPassword}
                  iconSize={heightClass}
                />
              )}

              {rightAreaElement?.()}
            </RightAreaElementContainer>
          )}
        </InputStructure>

        {mouseOver?.enabled && (
          <MouseOver elementRef={inputRef} content={mouseOver.content} />
        )}
      </ComponentStructure>
    )
  }
)

Input.displayName = 'Input'

export default Input
